import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import searchIcon from "../images/Search.svg";
import { fetchHelpArticles, HELP_ARTICLE_PAGE_SIZE, HELP_ARTICLE_SEARCH_MAX_LENGTH } from "../services/helpService";
import type { HelpArticleSummary } from "../types/help";
import { gettingStartedRoleLabel } from "../utils/gettingStartedRole";
import "../styles/getting-started.css";
import "../styles/pagination.css";

const SEARCH_DEBOUNCE_MS = 300;
const MAX_PAGE_BUTTONS = 15;
const LOAD_ERROR_MESSAGE =
    "Unable to load getting started articles. Please try again shortly.";
const EMPTY_MESSAGE = "No getting started articles are available right now.";
const SEARCH_EMPTY_MESSAGE = "No articles match your search.";

export default function GettingStartedPage() {
    const [articles, setArticles] = useState<HelpArticleSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const roleLabel = gettingStartedRoleLabel();

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            const nextQuery = searchInput.trim().slice(0, HELP_ARTICLE_SEARCH_MAX_LENGTH);
            setSearchQuery((current) => {
                if (current !== nextQuery) {
                    setPage(0);
                }
                return nextQuery;
            });
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setIsLoading(true);
            setLoadError(false);
            try {
                const result = await fetchHelpArticles({
                    page,
                    size: HELP_ARTICLE_PAGE_SIZE,
                    search: searchQuery,
                });
                if (!isMounted) {
                    return;
                }
                const nextArticles = result.articles ?? [];
                setArticles(nextArticles);
                setTotal(typeof result.total === "number" ? result.total : nextArticles.length);
            } catch {
                if (isMounted) {
                    setArticles([]);
                    setTotal(0);
                    setLoadError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        void load();
        return () => {
            isMounted = false;
        };
    }, [page, searchQuery]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value.slice(0, HELP_ARTICLE_SEARCH_MAX_LENGTH));
    }, []);

    const handlePageChange = useCallback((nextPage: number) => {
        setPage(nextPage);
    }, []);

    const articleCountLabel = `${total} ${total === 1 ? "article" : "articles"}`;
    const totalPages = Math.max(1, Math.ceil(total / HELP_ARTICLE_PAGE_SIZE));
    const showPagination = !isLoading && !loadError && total > HELP_ARTICLE_PAGE_SIZE;
    const pageStart = total === 0 ? 0 : page * HELP_ARTICLE_PAGE_SIZE + 1;
    const pageEnd = Math.min((page + 1) * HELP_ARTICLE_PAGE_SIZE, total);
    const showPageNumbers = totalPages <= MAX_PAGE_BUTTONS;

    return (
        <main className="getting-started-page" data-testid="getting-started-page">
            <div className="getting-started-header">
                <div>
                    <h1 className="getting-started-title">Help Articles</h1>
                    <p className="getting-started-subtitle">
                        These articles will help you get onboarded.
                    </p>
                </div>
                <span className="getting-started-role-badge" data-testid="getting-started-role-badge">
                    {roleLabel}
                </span>
            </div>

            <div className="getting-started-toolbar">
                <p className="getting-started-count" data-testid="getting-started-article-count">
                    {!isLoading && !loadError ? articleCountLabel : ""}
                </p>
                <label className="getting-started-search">
                    <span className="visually-hidden">Search articles</span>
                    <input
                        type="search"
                        className="getting-started-search-input"
                        placeholder="Search"
                        value={searchInput}
                        onChange={handleSearchChange}
                        maxLength={HELP_ARTICLE_SEARCH_MAX_LENGTH}
                        data-testid="getting-started-search"
                    />
                    <img src={searchIcon} alt="" className="getting-started-search-icon" />
                </label>
            </div>

            {isLoading && (
                <p className="getting-started-status" data-testid="getting-started-loading">
                    Loading articles…
                </p>
            )}

            {!isLoading && loadError && (
                <p className="getting-started-status getting-started-status--error" data-testid="getting-started-error">
                    {LOAD_ERROR_MESSAGE}
                </p>
            )}

            {!isLoading && !loadError && total === 0 && !searchQuery && (
                <p className="getting-started-status" data-testid="getting-started-empty">
                    {EMPTY_MESSAGE}
                </p>
            )}

            {!isLoading && !loadError && total === 0 && Boolean(searchQuery) && (
                <p className="getting-started-status" data-testid="getting-started-search-empty">
                    {SEARCH_EMPTY_MESSAGE}
                </p>
            )}

            {!isLoading && !loadError && articles.length > 0 && (
                <ul className="getting-started-article-list">
                    {articles.map((article) => (
                        <li key={article.slug}>
                            <Link
                                to={`/getting-started/${article.slug}`}
                                className="getting-started-article-card"
                                data-testid={`getting-started-article-card-${article.slug}`}
                            >
                                <span className="getting-started-article-card-copy">
                                    <span className="getting-started-article-card-title">{article.title}</span>
                                    {article.summary && (
                                        <span className="getting-started-article-card-summary">
                                            {article.summary}
                                        </span>
                                    )}
                                </span>
                                <span className="getting-started-article-card-chevron" aria-hidden="true">
                                    ›
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {showPagination && (
                <div className="pagination getting-started-pagination" data-testid="getting-started-pagination">
                    <div className="pagination-info">
                        Showing <strong>{pageStart}-{pageEnd}</strong> of <strong>{total}</strong> articles
                    </div>
                    <div className="pagination-controls">
                        <div className="page-nav">
                            <button
                                type="button"
                                className="page-btn"
                                onClick={() => handlePageChange(Math.max(page - 1, 0))}
                                disabled={page <= 0}
                                aria-label="Previous page"
                            >
                                Previous
                            </button>
                            {showPageNumbers && Array.from({ length: totalPages }, (_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`page-number ${index === page ? "page-number-active" : ""}`}
                                    onClick={() => handlePageChange(index)}
                                    aria-label={`Page ${index + 1}`}
                                    aria-current={index === page ? "page" : undefined}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="page-btn"
                                onClick={() => handlePageChange(Math.min(page + 1, totalPages - 1))}
                                disabled={page >= totalPages - 1}
                                aria-label="Next page"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
